import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatErr } from "@/lib/api";
import { startGoogleSignIn } from "@/lib/googleAuth";
import Logo from "@/components/Logo";
import { ArrowRight } from "@phosphor-icons/react";

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.9c1.7-1.57 2.7-3.88 2.7-6.61z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.83.87-3.06.87-2.35 0-4.34-1.59-5.05-3.72H.96v2.33A9 9 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.95 10.71a5.4 5.4 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.08l2.99-2.33z"/>
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.58-2.58C13.46.9 11.42 0 9 0A9 9 0 0 0 .96 4.96l2.99 2.33C4.66 5.17 6.65 3.58 9 3.58z"/>
    </svg>
  );
}


export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      const map = { admin: "/admin", school: "/school", parent: "/parent", agent: "/agent" };
      nav(map[u.role] || "/dashboard");
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-col w-1/2 bg-[#0A3622] text-white p-16 relative overflow-hidden">
        <Logo variant="dark" testid="login-logo" />
        <div className="mt-auto relative z-10">
          <div className="overline !text-[#F2B632] mb-4">School fees, paid your way</div>
          <h2 className="font-display text-4xl font-medium tracking-tight leading-tight">
            Pay school fees monthly.
            <br />
            <span className="italic">Your child starts term on time.</span>
          </h2>
          <p className="mt-6 text-white/70 text-sm leading-relaxed max-w-sm">
            Spread fees over 3 to 9 months. The school is paid in full on day one, and you see the full cost before you accept.
          </p>
        </div>
      </div>

      <div className="flex-1 grid place-items-center bg-[#F9F7F4] p-8">
        <form onSubmit={submit} className="w-full max-w-md card !p-10" data-testid="login-form">
          <div className="overline mb-3">Welcome back</div>
          <h1 className="font-display text-3xl font-medium tracking-tight text-[#132A13]">Sign in to your portal</h1>
          <p className="text-sm text-[#4F5D4F] mt-2">Schools, parents, and agents use the same login.</p>

          <div className="mt-8 space-y-4">
            {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <button
              type="button"
              onClick={() => { startGoogleSignIn(); }}
              className="w-full flex items-center justify-center gap-2 border border-[#E5E3DB] hover:border-[#0A3622] bg-white text-[#132A13] font-medium rounded-full py-3 px-4 transition-colors text-sm"
              data-testid="login-google-button"
            >
              <GoogleLogo /> Continue with Google
            </button>
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.14em] text-[#4F5D4F]/70">
              <div className="h-px flex-1 bg-[#E5E3DB]" />
              <span>or with email</span>
              <div className="h-px flex-1 bg-[#E5E3DB]" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">Email</label>
              <input className="input mt-1" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="login-email-input" />
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <label className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">Password</label>
                <Link to="/forgot-password" className="text-xs text-[#F2B632] hover:underline font-medium" data-testid="login-forgot-link">Forgot?</Link>
              </div>
              <input className="input mt-1" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="•••••••" data-testid="login-password-input" />
            </div>
            {err && <p className="text-sm text-[#C93B3B]" data-testid="login-error">{err}</p>}
            <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="login-submit-button">
              {loading ? "Signing in…" : <>Sign in <ArrowRight size={16} weight="bold" /></>}
            </button>
          </div>

          <p className="text-sm text-[#4F5D4F] mt-6 text-center">
            Don't have an account?{" "}
            <Link to="/register" className="text-[#F2B632] hover:underline font-medium" data-testid="login-register-link">Create one</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
